import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Stack, Button, Chip, CircularProgress, TextField, InputAdornment, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import apiService from '../../services/apiService';

const statusColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'paid':
      return 'success';
    case 'open':
    case 'due':
      return 'warning';
    case 'failed':
    case 'uncollectible':
      return 'error';
    default:
      return 'default';
  }
};

const BillingHistory = ({ currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoice, setLoadingInvoice] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        // Get billing history from backend (Paddle transactions only)
        const data = await apiService.getBillingHistory();
        // Handle both response formats: { billing_history: [...] } or { invoices: [...] } or direct array
        let rows = [];
        if (data?.billing_history && Array.isArray(data.billing_history)) {
          rows = data.billing_history;
        } else if (data?.invoices && Array.isArray(data.invoices)) {
          rows = data.invoices;
        } else if (Array.isArray(data)) {
          rows = data;
        }

        const userEmail = (currentUser?.email || '').toLowerCase().trim();
        if (userEmail) {
          rows = (rows || []).filter((inv) => {
            const invEmail = (
              inv.customer_email ||
              inv.metadata?.user_email ||
              inv.metadata?.customer_email ||
              ''
            )
              .toLowerCase()
              .trim();
            return !invEmail || invEmail === userEmail;
          });
        }

        setInvoices(rows || []);
      } catch (e) {
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser?.email]);

  const fmt = (n, currency = 'USD') => `${currency} ${Number(n || 0).toFixed(2)}`;
  // ***** BH23072026_1300 — plan/cycle from invoice/DB fields only (no hardcoded amount→plan map)

  const inferFromDescription = (inv) => {
    const txt = String(inv.description || '').toLowerCase();
    let planName = null;
    if (txt.includes('enterprise') || txt.includes('corporate')) planName = 'Enterprise';
    else if (txt.includes('professional') || txt.includes('basic') || txt.includes('pro')) planName = 'Professional';
    else if (txt.includes('ultimate')) planName = 'Developer Ultimate';
    else if (txt.includes('plus')) planName = 'Developer Plus';
    else if (txt.includes('pay-as-you-go') || txt.includes('pay as you go')) planName = 'Pay-as-you-go';
    else if (
      txt.includes('community') ||
      txt.includes('starter') ||
      txt.includes('trial') ||
      txt.includes('free')
    ) {
      planName = 'Community Edition';
    }

    let billingCycle = null;
    if (txt.includes('year') || txt.includes('annual') || txt.includes('/yr')) billingCycle = 'Yearly';
    else if (txt.includes('month') || txt.includes('/mo')) billingCycle = 'Monthly';

    return { planName, billingCycle };
  };

  const getPlanName = (inv) =>
    inv.plan_name ||
    inv.plan ||
    inv.metadata?.plan_name ||
    inv.metadata?.plan ||
    inv.details?.plan_name ||
    inferFromDescription(inv).planName ||
    'N/A';

  const getBillingCycle = (inv) => {
    const rawCycle =
      inv.billing_cycle ||
      inv.metadata?.billing_cycle ||
      inv.details?.billing_cycle ||
      '';
    const normalized = String(rawCycle).toLowerCase().trim();
    if (['year', 'yearly', 'annual', 'annually', '12m', '12_month'].includes(normalized)) {
      return 'Yearly';
    }
    if (['month', 'monthly', '1m', '1_month'].includes(normalized)) {
      return 'Monthly';
    }
    if (normalized) return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    return (
      inferFromDescription(inv).billingCycle ||
      'N/A'
    );
  };

  // Filter invoices based on search term and date range
  const filteredInvoices = invoices.filter((inv, idx) => {
    const invoiceNumber = inv.invoice_number || inv.paddle_invoice_id || inv.transaction_id || `Paddle-${idx+1}`;
    const invoiceDate = inv.invoice_date || inv.created_at || inv.billed_at || null;
    const amount = inv.amount_paid ?? inv.amount_due ?? inv.total ?? inv.amount ?? 0;
    const currency = inv.currency || 'USD';
    const status = inv.status || 'completed';
    const planName = getPlanName(inv);
    const billingCycle = getBillingCycle(inv);

    // 1. Search term filter
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      const matchNumber = String(invoiceNumber).toLowerCase().includes(term);
      const matchStatus = String(status).toLowerCase().includes(term);
      const matchAmount = String(amount).toLowerCase().includes(term);
      const matchCurrency = String(currency).toLowerCase().includes(term);
      const matchPlanName = String(planName).toLowerCase().includes(term);
      const matchBillingCycle = String(billingCycle).toLowerCase().includes(term);
      if (!matchNumber && !matchStatus && !matchAmount && !matchCurrency && !matchPlanName && !matchBillingCycle) {
        return false;
      }
    }

    // 2. Date range filter
    if (invoiceDate) {
      const dateStr = String(invoiceDate).slice(0, 10); // Format YYYY-MM-DD
      if (startDate && dateStr < startDate) {
        return false;
      }
      if (endDate && dateStr > endDate) {
        return false;
      }
    } else if (startDate || endDate) {
      return false;
    }

    return true;
  });

  return (
    <Box className="candor-billing">
      <Paper variant="outlined" className="candor-filter-bar" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search invoice number, amount, or status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                )
              }
            }}
          />
          <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}>
            <TextField
              size="small"
              label="From Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ width: '150px' }}
            />
            <TextField
              size="small"
              label="To Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ width: '150px' }}
            />
            {(searchTerm || startDate || endDate) && (
              <Button 
                size="small" 
                variant="outlined" 
                color="secondary"
                onClick={() => {
                  setSearchTerm('');
                  setStartDate('');
                  setEndDate('');
                }}
                sx={{ textTransform: 'none', px: 2 }}
              >
                Clear
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Box 
            component="div" 
            sx={{ 
              width: '100%', 
              maxHeight: '460px', // Restricts display to approximately 10 rows
              overflowY: 'auto',
              overflowX: 'auto',
              position: 'relative'
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ position: 'sticky', top: 0, backgroundColor: '#ffffff', zIndex: 10, textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,0.1)', width: '60px' }}>S.No</th>
                  <th style={{ position: 'sticky', top: 0, backgroundColor: '#ffffff', zIndex: 10, textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>Invoice</th>
                  <th style={{ position: 'sticky', top: 0, backgroundColor: '#ffffff', zIndex: 10, textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>Date</th>
                  <th style={{ position: 'sticky', top: 0, backgroundColor: '#ffffff', zIndex: 10, textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>Plan Name</th>
                  <th style={{ position: 'sticky', top: 0, backgroundColor: '#ffffff', zIndex: 10, textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>Billing Cycle</th>
                  <th style={{ position: 'sticky', top: 0, backgroundColor: '#ffffff', zIndex: 10, textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>Amount</th>
                  <th style={{ position: 'sticky', top: 0, backgroundColor: '#ffffff', zIndex: 10, textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>Status</th>
                  <th style={{ position: 'sticky', top: 0, backgroundColor: '#ffffff', zIndex: 10, textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,0.1)' }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ padding: '20px 8px', textAlign: 'center', color: 'rgba(0,0,0,0.6)' }}>
                      No invoices found matching the filters.
                    </td>
                  </tr>
                )}
                {filteredInvoices.map((inv, idx) => {
                  // Use ONLY Paddle invoice data - no local invoice generation
                  const invoiceNumber = inv.invoice_number || inv.paddle_invoice_id || inv.transaction_id || `Paddle-${idx+1}`;
                  const invoiceDate = inv.invoice_date || inv.created_at || inv.billed_at || null;
                  const amount = inv.amount_paid ?? inv.amount_due ?? inv.total ?? inv.amount ?? 0;
                  const currency = inv.currency || 'USD';
                  const status = inv.status || 'completed';
                  const planName = getPlanName(inv);
                  const billingCycle = getBillingCycle(inv);
                  // Use stored invoice URL if available, otherwise fetch on-demand from Paddle API
                  const hostedPdfUrl = inv.invoice_url || inv.hosted_invoice_url || inv.paddle_invoice_url || null;
                  const transactionId = inv.transaction_id || inv.paddle_transaction_id || null;

                  return (
                    <tr key={invoiceNumber}>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,0.06)', color: 'rgba(0,0,0,0.6)' }}>{idx + 1}</td>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>{invoiceNumber}</td>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                        {invoiceDate ? String(invoiceDate).slice(0, 10) : 'N/A'}
                      </td>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>{planName}</td>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>{billingCycle}</td>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,0.06)', textAlign: 'right' }}>{fmt(amount, currency)}</td>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                        <Chip size="small" color={statusColor(status)} label={String(status).toUpperCase()} />
                      </td>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid rgba(0,0,0,0.06)', textAlign: 'right' }}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!transactionId || loadingInvoice[transactionId]}
                          onClick={async () => {
                            try {
                              // If we already have a stored invoice URL, use it
                              if (hostedPdfUrl) {
                                window.open(hostedPdfUrl, '_blank');
                                return;
                              }
                              
                              // Otherwise, fetch invoice URL from Paddle API on-demand
                              if (transactionId) {
                                setLoadingInvoice(prev => ({ ...prev, [transactionId]: true }));
                                try {
                                  const invoiceResponse = await apiService.getTransactionInvoice(transactionId);
                                  if (invoiceResponse?.invoice_url) {
                                    window.open(invoiceResponse.invoice_url, '_blank');
                                  } else {
                                    alert('Invoice URL not available for this transaction');
                                  }
                                } finally {
                                  setLoadingInvoice(prev => ({ ...prev, [transactionId]: false }));
                                }
                              } else {
                                alert('Transaction ID not available');
                              }
                            } catch (error) {
                              // Handle both axios error format and handleError format
                              const errorMessage = error.response?.data?.detail 
                                || error.data?.detail 
                                || error.message 
                                || 'Failed to retrieve invoice. Please try again.';
                              alert(errorMessage);
                              setLoadingInvoice(prev => ({ ...prev, [transactionId]: false }));
                            }
                          }}
                        >
                          {loadingInvoice[transactionId] 
                            ? 'Loading...' 
                            : hostedPdfUrl 
                              ? 'Download from Paddle' 
                              : 'Get Invoice from Paddle'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default BillingHistory;
